import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  forwardRef,
  HostListener,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
} from '@angular/forms';
import { Subject, debounceTime, takeUntil, distinctUntilChanged } from 'rxjs';
import { StaffApiService, StaffMember } from 'src/app/core/services/api/staff-api.service';

export interface MentionData {
  content: string;
  mentionedUserIds: number[];
}

@Component({
  selector: 'app-mention-input',
  templateUrl: './mention-input.component.html',
  styleUrls: ['./mention-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MentionInputComponent),
      multi: true,
    },
  ],
})
export class MentionInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() placeholder: string = 'Enter note...';
  @Input() rows: number = 4;
  @Output() mentionedUsersChange = new EventEmitter<number[]>();

  @ViewChild('textarea') textarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('dropdown') dropdown!: ElementRef<HTMLDivElement>;

  content: string = '';
  showDropdown: boolean = false;
  suggestions: StaffMember[] = [];
  selectedIndex: number = 0;
  isLoading: boolean = false;
  mentionedUsers: Map<number, StaffMember> = new Map();
  mentionStartIndex: number = -1;
  searchQuery: string = '';

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  dropdownPosition = { top: 0, left: 0 };

  constructor(private staffApiService: StaffApiService) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        this.searchStaff(query);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string): void {
    this.content = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.content = target.value;
    this.onChange(this.content);

    this.checkForMention(target);
    this.extractMentionedUsers();
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.showDropdown) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.suggestions.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case 'Enter':
      case 'Tab':
        if (this.suggestions.length > 0) {
          event.preventDefault();
          this.selectSuggestion(this.suggestions[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.closeDropdown();
        break;
    }
  }

  onBlur(): void {
    this.onTouched();
    // Delay closing to allow click on suggestion
    setTimeout(() => {
      this.closeDropdown();
    }, 200);
  }

  private checkForMention(textarea: HTMLTextAreaElement): void {
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = this.content.substring(0, cursorPosition);

    // Find the last @ symbol that's not part of a completed mention
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.closeDropdown();
      return;
    }

    // Check if there's a space between @ and cursor (means mention was cancelled)
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

    // If there's a newline or the @ is at a word boundary, check for mention
    const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
    const isValidMentionStart =
      charBeforeAt === ' ' ||
      charBeforeAt === '\n' ||
      charBeforeAt === '\t' ||
      lastAtIndex === 0;

    if (!isValidMentionStart) {
      this.closeDropdown();
      return;
    }

    // If there's no space in the text after @, it's a potential mention
    if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
      this.mentionStartIndex = lastAtIndex;
      this.searchQuery = textAfterAt;
      this.showDropdown = true;
      this.selectedIndex = 0;
      this.updateDropdownPosition(textarea);
      this.searchSubject.next(textAfterAt);
    } else {
      this.closeDropdown();
    }
  }

  private updateDropdownPosition(textarea: HTMLTextAreaElement): void {
    // Get cursor position for dropdown placement
    const textareaRect = textarea.getBoundingClientRect();
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;

    // Calculate approximate position based on text content
    const textBeforeCursor = this.content.substring(
      0,
      textarea.selectionStart
    );
    const lines = textBeforeCursor.split('\n');
    const currentLineIndex = lines.length - 1;
    const currentLine = lines[currentLineIndex];

    // Approximate character width (this is a rough estimate)
    const charWidth = 8;
    const leftOffset = Math.min(
      (currentLine.length - this.searchQuery.length - 1) * charWidth,
      textarea.clientWidth - 250
    );

    this.dropdownPosition = {
      top: Math.min(
        (currentLineIndex + 1) * lineHeight + 4,
        textarea.clientHeight - 150
      ),
      left: Math.max(leftOffset, 0),
    };
  }

  private searchStaff(query: string): void {
    this.isLoading = true;
    this.staffApiService
      .searchStaff(query, 10)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.suggestions = response.data || [];
          this.isLoading = false;
          this.selectedIndex = 0;
        },
        error: () => {
          this.suggestions = [];
          this.isLoading = false;
        },
      });
  }

  selectSuggestion(staff: StaffMember): void {
    if (!staff || this.mentionStartIndex === -1) {
      return;
    }

    const textarea = this.textarea.nativeElement;
    const cursorPosition = textarea.selectionStart;

    // Create the mention text
    const mentionText = `@${staff.first_name} ${staff.last_name}`;

    // Replace the @query with the full mention
    const beforeMention = this.content.substring(0, this.mentionStartIndex);
    const afterCursor = this.content.substring(cursorPosition);

    this.content = beforeMention + mentionText + ' ' + afterCursor;
    this.onChange(this.content);

    // Add to mentioned users
    this.mentionedUsers.set(staff.id, staff);
    this.emitMentionedUsers();

    // Update cursor position
    setTimeout(() => {
      const newPosition = beforeMention.length + mentionText.length + 1;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    });

    this.closeDropdown();
  }

  private extractMentionedUsers(): void {
    // Extract all @mentions from content and validate against known users
    const mentionPattern = /@([A-Za-z]+\s[A-Za-z]+)/g;
    const matches = this.content.matchAll(mentionPattern);
    const currentMentions = new Set<number>();

    for (const match of matches) {
      const fullName = match[1];
      // Find the user in our mentioned users map
      for (const [id, user] of this.mentionedUsers) {
        if (`${user.first_name} ${user.last_name}` === fullName) {
          currentMentions.add(id);
          break;
        }
      }
    }

    // Remove users that are no longer mentioned
    for (const id of this.mentionedUsers.keys()) {
      if (!currentMentions.has(id)) {
        this.mentionedUsers.delete(id);
      }
    }

    this.emitMentionedUsers();
  }

  private emitMentionedUsers(): void {
    const userIds = Array.from(this.mentionedUsers.keys());
    this.mentionedUsersChange.emit(userIds);
  }

  private closeDropdown(): void {
    this.showDropdown = false;
    this.suggestions = [];
    this.mentionStartIndex = -1;
    this.searchQuery = '';
  }

  getMentionedUserIds(): number[] {
    return Array.from(this.mentionedUsers.keys());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      !this.textarea?.nativeElement.contains(target) &&
      !this.dropdown?.nativeElement?.contains(target)
    ) {
      this.closeDropdown();
    }
  }
}

